import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SectionHeading from "./SectionHeading";
import LangLink from "../shared/LangLink";
import { StaggerGroup, StaggerItem } from "../shared/Reveal";
import { PROCEDURE_CATEGORIES } from "../../data/procedures";
import { useI18n } from "../../i18n";

export default function ProcedureCategories() {
  const { t, langPath } = useI18n();
  return (
    <section className="bg-surface py-20 md:py-28 lg:py-36" data-testid="procedure-categories">
      <div className="container-psv">
        <SectionHeading
          eyebrow={t("nav.procedures")}
          heading="What procedure are you considering?"
          intro="Explore the main procedure areas or request guidance if you are unsure where to begin."
        />

        <StaggerGroup className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PROCEDURE_CATEGORIES.map((cat) => (
            <StaggerItem
              key={cat.slug}
              className={cat.featured ? "md:col-span-2 lg:col-span-1 lg:row-span-2" : ""}
            >
              <LangLink
                to={`/${cat.slug}`}
                aria-label={`Explore ${cat.title.toLowerCase()} surgery options in Vietnam`}
                className="group block h-full bg-background border border-border rounded-lg overflow-hidden hover:border-accent transition-colors duration-300"
                data-testid={`category-card-${cat.key}`}
              >
                <div className="relative overflow-hidden h-52">
                  <motion.img
                    src={cat.image}
                    alt={`${cat.title} plastic surgery in Vietnam`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  />
                  {cat.featured && (
                    <span className="absolute top-4 left-4 bg-accent text-accent-foreground text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full font-sans">
                      Often overlooked
                    </span>
                  )}
                </div>
                <div className="p-7">
                  <h3 className="font-serif text-3xl text-primary mb-2">{cat.title}</h3>
                  <p className="text-sm text-muted-foreground mb-5">{cat.intro}</p>
                  <ul className="space-y-1.5 mb-6">
                    {cat.items.map((it) => (
                      <li key={it} className="text-[14px] text-secondary-foreground flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-accent" /> {it}
                      </li>
                    ))}
                  </ul>
                  <span className="inline-flex items-center gap-1.5 text-accent font-medium text-sm font-sans group-hover:gap-2.5 transition-all">
                    {t("cta.exploreOptions")} <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </LangLink>
            </StaggerItem>
          ))}
        </StaggerGroup>

        <p className="mt-8 text-xs text-muted-foreground max-w-3xl">
          Not every listed procedure is suitable or currently available through every provider. Suitability is confirmed only after an assessment by a licensed medical professional.
        </p>
      </div>
    </section>
  );
}
